import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Icon } from '@plone/volto/components';
import { Button, Grid } from 'semantic-ui-react';
import {
  VoltoErrorIcon,
  VoltoInfoIcon,
  VoltoSuccessIcon,
  VoltoWarningIcon,
  VoltoCircleDismissIcon,
  VoltoCircleRightIcon,
} from 'volto-epics-addon/icons';
import { useIntl, defineMessages } from 'react-intl';
import { EPIC_TOAST_CONTINUATION } from 'volto-epics-addon/constants';
import ButtonLink from 'volto-epics-addon/components/theme/Button/ButtonLink';

const messages = defineMessages({
  wantToContinue: {
    id: 'wantToContinue',
    defaultMessage: "If you don't want to continue, please click",
  },
  cancel: {
    id: 'cancel',
    defaultMessage: 'cancel',
  },
  continue: {
    id: 'continue',
    defaultMessage: 'Continue',
  },
  wantToCancel: {
    id: 'wantToCancel',
    defaultMessage: 'Do you really want to cancel?',
  },
  yesCancel: {
    id: 'yesCancel',
    defaultMessage: 'Yes, cancel',
  },
  noContinue: {
    id: 'noContinue',
    defaultMessage: 'No, continue',
  },
  clickContinueTo: {
    id: 'clickContinueTo',
    defaultMessage: 'Click continue to proceed previous action or cancel it.',
  },
});

const ContinuationToast = (props) => {
  function getIcon(props) {
    if (props.info) {
      return VoltoInfoIcon;
    } else if (props.success) {
      return VoltoSuccessIcon;
    } else if (props.error) {
      return VoltoErrorIcon;
    } else if (props.error) {
      return VoltoWarningIcon;
    } else {
      return VoltoSuccessIcon;
    }
  }

  const { type, onContinue, onCancel, title, content, children } = props;

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [loading, setLoading] = useState(false);

  const intl = useIntl();

  // title property value also used to display error message, the value can be str as well as MessageDescriptor object ({id: id, defaultMessage: message})
  let _title;

  try {
    // If MessageDescriptor
    const titleJson = JSON.parse(title);
    _title = intl.formatMessage(titleJson);
  } catch (e) {
    _title = title;
  }

  const cancelLink = confirmCancel ? (
    <Button
      className="toast-button"
      loading={loading}
      onClick={async (e) => {
        if (!loading) {
          setLoading(true);
          await onCancel(e);
        }
      }}
    >
      <div className="toast-button-content">
        <Icon name={VoltoCircleDismissIcon} size="24px" color="red" />
        <span className="toast-button-text">
          {intl.formatMessage(messages.yesCancel)}
        </span>
      </div>
    </Button>
  ) : (
    <Button className="toast-button" onClick={() => setConfirmCancel(true)}>
      <div className="toast-button-content">
        <Icon name={VoltoCircleDismissIcon} size="24px" />
        <span className="toast-button-text">
          {intl.formatMessage(messages.cancel)}
        </span>
      </div>
    </Button>
  );

  const continueLink = confirmCancel ? (
    <Button
      className="toast-button"
      disabled={loading}
      onClick={() => {
        setLoading(true);
        toast.dismiss(EPIC_TOAST_CONTINUATION);
      }}
    >
      <div className="toast-button-content">
        <Icon name={VoltoCircleRightIcon} size="24px" />
        <span className="toast-button-text">
          {intl.formatMessage(messages.noContinue)}
        </span>
      </div>
    </Button>
  ) : (
    <Button className="toast-button" onClick={onContinue}>
      <div className="toast-button-content">
        <Icon name={VoltoCircleRightIcon} size="24px" />
        <span className="toast-button-text">
          {intl.formatMessage(messages.continue)}
        </span>
      </div>
    </Button>
  );

  return (
    <>
      <Grid className="toast-grid-container" textAlign="center">
        <>
          {!props.basic && (
            <Grid.Row className="toast-icon-row">
              <Grid.Column>
                <Icon
                  name={getIcon(props)}
                  className={
                    props.error
                      ? 'error'
                      : props.info
                      ? 'info'
                      : props.success
                      ? 'success'
                      : 'default'
                  }
                  color="#012957"
                  size="40px"
                />
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row style={{ padding: '1em 0 0.7em 0' }}>
            <Grid.Column>
              <Grid>
                {!onCancel || !!props.error ? (
                  <Grid.Row>
                    <Grid.Column>
                      <h4>{_title}</h4>
                    </Grid.Column>
                  </Grid.Row>
                ) : null}
                <Grid.Row
                  style={
                    !!onContinue || (!!onCancel && confirmCancel)
                      ? {
                          padding: '0.3em 0 0.6em 0px',
                        }
                      : null
                  }
                >
                  <Grid.Column>
                    {type === 'continue' ? (
                      <p style={{ color: '#012957' }}>
                        {intl.formatMessage(messages.clickContinueTo)}
                      </p>
                    ) : !!onCancel ? (
                      confirmCancel ? (
                        <p style={{ color: '#012957' }}>
                          {intl.formatMessage(messages.wantToCancel)}
                        </p>
                      ) : (
                        <p style={{ color: '#012957' }}>
                          {intl.formatMessage(messages.wantToContinue)}{' '}
                          <ButtonLink onClick={() => setConfirmCancel(true)}>
                            {intl.formatMessage(messages.cancel)}
                          </ButtonLink>
                          {'.'}
                        </p>
                      )
                    ) : (
                      <>{content && <p>{content}</p>}</>
                    )}
                  </Grid.Column>
                </Grid.Row>
                {(!!onContinue || (!!onCancel && confirmCancel)) && (
                  <Grid.Row
                    columns={!!onCancel ? 'equal' : '1'}
                    textAlign="center"
                    style={{ padding: '0.4em 0 0 0' }}
                  >
                    {!!onContinue && <Grid.Column>{continueLink}</Grid.Column>}
                    {!!onCancel && confirmCancel && (
                      <>
                        <Grid.Column>{cancelLink}</Grid.Column>
                        <Grid.Column>{continueLink}</Grid.Column>
                      </>
                    )}
                  </Grid.Row>
                )}
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </>
      </Grid>
    </>
  );
};

export default ContinuationToast;
